/**
 * Membership Tier Governance — Single Source of Truth
 *
 * Canonical definition of tier privileges. Every component, hook, and edge
 * function that needs to know "what can this tier do?" MUST import from here.
 *
 * Tier values map 1:1 to `users_membership_plans.tier` column values.
 */

// ---------------------------------------------------------------------------
// Tier identifiers
// ---------------------------------------------------------------------------

export const MEMBERSHIP_TIERS = ['free', 'courtesy', 'featured', 'expert', 'professional', 'business'] as const;

export type MembershipTier = (typeof MEMBERSHIP_TIERS)[number];

// ---------------------------------------------------------------------------
// Card layout variants
// ---------------------------------------------------------------------------

export type CardLayout = 'standard' | 'horizontal';

// ---------------------------------------------------------------------------
// Privilege definition per tier
// ---------------------------------------------------------------------------

export interface TierPrivileges {
  /** Show ✓ badge after display name */
  verified_badge: boolean;
  /** Card rendering variant */
  card_layout: CardLayout;
  /** Sorting boost: higher = listed first among verified users */
  listing_priority_boost: number;
  /** Max business (company) profiles the user may create */
  max_business_profiles: number;
  /** Eligible for featured / promoted placement sections */
  featured_placement: boolean;
  /** Whether this tier grants access to members-only content */
  content_access: boolean;
}

export const TIER_PRIVILEGES: Record<MembershipTier, TierPrivileges> = {
  free: {
    verified_badge: false,
    card_layout: 'standard',
    listing_priority_boost: 0,
    max_business_profiles: 1,
    featured_placement: false,
    content_access: false,
  },
  courtesy: {
    verified_badge: false,
    card_layout: 'standard',
    listing_priority_boost: 0,
    max_business_profiles: 1,
    featured_placement: false,
    content_access: false,
  },
  featured: {
    verified_badge: true,
    card_layout: 'standard',
    listing_priority_boost: 0,
    max_business_profiles: 1,
    featured_placement: false,
    content_access: false,
  },
  expert: {
    verified_badge: true,
    card_layout: 'standard',
    listing_priority_boost: 1,
    max_business_profiles: 1,
    featured_placement: false,
    content_access: false,
  },
  professional: {
    verified_badge: true,
    card_layout: 'horizontal',
    listing_priority_boost: 2,
    max_business_profiles: 1,
    featured_placement: true,
    content_access: true,
  },
  business: {
    verified_badge: true,
    card_layout: 'horizontal',
    listing_priority_boost: 2,
    max_business_profiles: 5,
    featured_placement: true,
    content_access: true,
  },
} as const;

// ---------------------------------------------------------------------------
// Legacy tier aliases (WP/PayPal migrated plans)
// Maps DB tier strings to canonical privilege sets
// ---------------------------------------------------------------------------

const LEGACY_TIER_MAP: Record<string, MembershipTier> = {
  brl_1: 'professional',
  brl_5: 'professional',
  brl_6: 'professional',
  brl_3: 'professional',
  brl_7: 'professional',
  brl_8: 'professional',
  brl_9: 'expert',
  brl_4: 'courtesy',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve tier privileges from a tier string (e.g. from DB).
 * Handles both canonical tiers and legacy aliases (brl_*).
 * Returns `free` privileges if tier is null/undefined/unknown.
 */
export function getTierPrivileges(tier: string | null | undefined): TierPrivileges {
  if (!tier) return TIER_PRIVILEGES.free;
  const key = tier.toLowerCase();
  // Check canonical tiers first, then legacy aliases
  if (key in TIER_PRIVILEGES) return TIER_PRIVILEGES[key as MembershipTier];
  const mapped = LEGACY_TIER_MAP[key];
  return mapped ? TIER_PRIVILEGES[mapped] : TIER_PRIVILEGES.free;
}

/**
 * Check if a tier string represents a "premium" tier (horizontal card).
 */
export function isPremiumTier(tier: string | null | undefined): boolean {
  return getTierPrivileges(tier).card_layout === 'horizontal';
}

/**
 * Check if a tier grants access to members-only content.
 * Only professional and business tiers have content access.
 */
export function hasContentAccess(tier: string | null | undefined): boolean {
  return getTierPrivileges(tier).content_access;
}

// ---------------------------------------------------------------------------
// Plan ID → Tier fallback map
// Static map so content-access checks survive users_membership_plans timeouts.
// ---------------------------------------------------------------------------

export const PLAN_ID_TO_TIER: Record<string, string> = {
  'e08fac1a-cd47-46f3-9de2-34fc753a7687': 'brl_1',        // Assinatura Mensal
  '12de4ed9-1ede-4e82-8c2e-b70d66391e30': 'brl_5',        // 14 Dias Gratuitos
  '0350cbee-0e00-4f85-aa26-9efd2e75844a': 'brl_6',        // Assinatura Mensal + Perfil Verificado
  '0b1f05f3-8937-4c85-873a-bc293ba6d577': 'brl_7',        // PRO Mensal
  'ddff2cb0-bcd6-42f2-a225-de1353b03805': 'brl_8',        // Experimente + PRO Mensal
  '8b45f880-5aab-4052-a24b-70d9854a2119': 'brl_9',        // Especialista
  '9db459d3-ddb0-4043-801d-ee0a4314c829': 'brl_3',        // PRO Anual
  '2bc3834e-9d83-4963-941b-da8c91d8d06d': 'brl_4',        // Colaboradores
  '45898ab3-c2c9-42ff-bbc6-03df78689038': 'business',     // Business
  'ca23de8c-cc79-4c57-971b-2a5a484617f0': 'expert',       // Expert
  'eb1b028c-243b-4a85-9d78-3c247c27a58f': 'professional', // Professional
};

export const PLAN_ID_TO_NAME: Record<string, string> = {
  'e08fac1a-cd47-46f3-9de2-34fc753a7687': 'Assinatura Mensal',
  '12de4ed9-1ede-4e82-8c2e-b70d66391e30': '14 Dias Gratuitos',
  '0350cbee-0e00-4f85-aa26-9efd2e75844a': 'Assinatura Mensal + Perfil Verificado',
  '0b1f05f3-8937-4c85-873a-bc293ba6d577': 'PRO Mensal',
  'ddff2cb0-bcd6-42f2-a225-de1353b03805': 'Experimente + PRO Mensal',
  '8b45f880-5aab-4052-a24b-70d9854a2119': 'Especialista',
  '9db459d3-ddb0-4043-801d-ee0a4314c829': 'PRO Anual',
  '2bc3834e-9d83-4963-941b-da8c91d8d06d': 'Colaboradores',
  '45898ab3-c2c9-42ff-bbc6-03df78689038': 'Business',
  'ca23de8c-cc79-4c57-971b-2a5a484617f0': 'Expert',
  'eb1b028c-243b-4a85-9d78-3c247c27a58f': 'Professional',
};

// ---------------------------------------------------------------------------
// Legacy plan → active plan UI mapping
// Maps inactive legacy plan IDs to their current active equivalent for display
// ---------------------------------------------------------------------------

export const LEGACY_PLAN_DISPLAY_MAP: Record<string, string> = {
  'e08fac1a-cd47-46f3-9de2-34fc753a7687': '0b1f05f3-8937-4c85-873a-bc293ba6d577', // brl_1 Assinatura Mensal → PRO Mensal
  '12de4ed9-1ede-4e82-8c2e-b70d66391e30': '0b1f05f3-8937-4c85-873a-bc293ba6d577', // brl_5 14 Dias Gratuitos → PRO Mensal
  '0350cbee-0e00-4f85-aa26-9efd2e75844a': '0b1f05f3-8937-4c85-873a-bc293ba6d577', // brl_6 Assinatura + Perfil → PRO Mensal
  'ddff2cb0-bcd6-42f2-a225-de1353b03805': '0b1f05f3-8937-4c85-873a-bc293ba6d577', // brl_8 Experimente+PRO → PRO Mensal
};

/** Maps legacy plan IDs to the display name of their active equivalent */
export const LEGACY_PLAN_NAME_MAP: Record<string, string> = {
  'e08fac1a-cd47-46f3-9de2-34fc753a7687': 'PRO Mensal',
  '12de4ed9-1ede-4e82-8c2e-b70d66391e30': 'PRO Mensal',
  '0350cbee-0e00-4f85-aa26-9efd2e75844a': 'PRO Mensal',
  'ddff2cb0-bcd6-42f2-a225-de1353b03805': 'PRO Mensal',
};

/**
 * All membership statuses that represent a live/active subscription.
 * Use this everywhere instead of ad-hoc arrays.
 */
export const ACTIVE_MEMBERSHIP_STATUSES = [
  'paid_active',
  'free_trial',
  'courtesy',
  'pending_cancellation',
  // Legacy values kept for backward compat with any residual data
  'active',
  'trialing',
  'pending',
  // 'paused' intentionally excluded: failed payment = no access
] as const;

export type ActiveMembershipStatus = (typeof ACTIVE_MEMBERSHIP_STATUSES)[number];

/**
 * Check if a membership status string represents an active membership.
 */
export function isActiveMembershipStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return (ACTIVE_MEMBERSHIP_STATUSES as readonly string[]).includes(status);
}

/**
 * Statuses where a user has a live subscription that can be cancelled — superset of
 * ACTIVE_MEMBERSHIP_STATUSES + payment-trouble statuses (past_due / unpaid /
 * incomplete / paused). Use this to gate the cancel button and the membership card,
 * NOT to gate access to paid features (use isActiveMembershipStatus for that).
 *
 * Mirror of CANCELLABLE_MEMBERSHIP_STATUSES in
 * supabase/functions/_shared/constants/membershipStatuses.ts. Keep the two in sync.
 */
export const CANCELLABLE_MEMBERSHIP_STATUSES = [
  ...ACTIVE_MEMBERSHIP_STATUSES,
  'paused',
  'past_due',
  'unpaid',
  'incomplete',
] as const;

export function isCancellableMembershipStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return (CANCELLABLE_MEMBERSHIP_STATUSES as readonly string[]).includes(status);
}

/**
 * Statuses that should be cancelled immediately (DELETE on Stripe) rather than
 * scheduled for the period end. These users have not paid through the current
 * period, so there's nothing to honor — cancel is immediate and local status
 * moves directly to 'churned'.
 *
 * Use this to vary the confirmation dialog copy on the cancel button.
 */
export function isImmediateCancel(status: string | null | undefined): boolean {
  if (!status) return false;
  return ['past_due', 'unpaid', 'incomplete', 'paused'].includes(status);
}

// ---------------------------------------------------------------------------
// Payment-provider governance
// ---------------------------------------------------------------------------

/**
 * Payment providers the platform can still actively manage (upgrade / downgrade
 * / cancel / billing portal) through the manage-subscription edge function.
 *
 * PagSeguro was retired from payment_gateway_accounts, but some memberships
 * linger in `paid_active` with a valid current_period_end and
 * `gateway_account_code = NULL`. Any manage-subscription call for those 400s
 * with NO_GATEWAY_ACCOUNT, so every button on the management UI is dead. We
 * exclude deprecated providers here and route those users to the new-sub flow
 * instead. ('courtesy' is internal, gateway-less, and intentionally included.)
 */
export const SUPPORTED_PAYMENT_PROVIDERS = ['stripe', 'paypal', 'courtesy'] as const;

export type SupportedPaymentProvider = (typeof SUPPORTED_PAYMENT_PROVIDERS)[number];

export function isSupportedPaymentProvider(provider: string | null | undefined): boolean {
  if (!provider) return false;
  return (SUPPORTED_PAYMENT_PROVIDERS as readonly string[]).includes(provider);
}

/**
 * Whether to render the membership management UI (change plan / cancel /
 * billing portal). Requires BOTH a cancellable status AND a still-supported
 * provider. Deprecated-gateway memberships (e.g. pagseguro,
 * gateway_account_code NULL) fail the provider check and fall through to the
 * plan selector / new-subscription flow — manage-subscription would 400 for
 * them. Entitlement (read access through current_period_end) is checked
 * separately and is unaffected by this gate.
 */
export function hasManageableMembership(
  status: string | null | undefined,
  provider: string | null | undefined,
): boolean {
  return isCancellableMembershipStatus(status) && isSupportedPaymentProvider(provider);
}

// ---------------------------------------------------------------------------
// Verification helper
// ---------------------------------------------------------------------------

/**
 * Column Relationship (Source of Truth):
 * - membership_tier: authoritative plan tier, synced by membership-lifecycle.ts
 * - profile_verified (DEPRECATED): legacy boolean, no longer read by frontend.
 *   Replaced everywhere by `isVerifiedTier(membership_tier)`.
 *
 * A user is "verified" if they have an active expert, professional, or
 * business tier. Free, courtesy, and featured tiers are not verified.
 */
const NON_VERIFIED_TIERS = ['free', 'courtesy', 'featured'];

export function isVerifiedTier(tier: string | null | undefined): boolean {
  if (tier == null) return false;
  return !NON_VERIFIED_TIERS.includes(tier.toLowerCase());
}
